import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productService from '../../services/productService';
import productTypeService from '../../services/productTypeService';
import homeSectionService from '../../services/homeSectionService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';

const baseSections = [
  { key: 'new-arrivals', labelEn: 'New Arrivals', labelAr: 'وصل حديثا' },
  { key: 'gift-sets', labelEn: 'Gift Sets', labelAr: 'الأطقم والهدايا' },
  { key: 'male', labelEn: "Men's Perfumes", labelAr: 'عطور الرجال' },
  { key: 'female', labelEn: "Women's Perfumes", labelAr: 'عطور النساء' },
  { key: 'designer', labelEn: 'Designer Perfumes', labelAr: 'العطور التجارية' },
  { key: 'niche', labelEn: 'Niche Perfumes', labelAr: 'العطور المتخصصة' },
  { key: 'top-sales', labelEn: 'Top Sellers', labelAr: 'الأكثر مبيعا' },
];

const isMenCategory = (product) => {
  const category = (product.categoryNameEn || '').toLowerCase();
  return (category.includes('men') || category.includes('male') || category.includes('man'))
    && !category.includes('women')
    && !category.includes('female')
    && !category.includes('woman');
};

const isWomenCategory = (product) => {
  const category = (product.categoryNameEn || '').toLowerCase();
  return category.includes('women') || category.includes('female') || category.includes('woman');
};

const matchesSelectedSection = (product, sectionKey) => {
  if (sectionKey.startsWith('product-type:')) {
    return product.productTypeId?.toLowerCase() === sectionKey.replace('product-type:', '').toLowerCase();
  }

  switch (sectionKey) {
    case 'new-arrivals':
      return product.isNew === true;
    case 'gift-sets':
      return product.kind === 1 || product.kind === 2;
    case 'male':
      return isMenCategory(product);
    case 'female':
      return isWomenCategory(product);
    case 'designer':
      return product.classification === 0;
    case 'niche':
      return product.classification === 1;
    default:
      return true;
  }
};

const HomeSectionsIndex = () => {
  const { isRtl } = useLocalization();
  const [products, setProducts] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [selectedSection, setSelectedSection] = useState('new-arrivals');
  const [sectionItems, setSectionItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');

  const sections = useMemo(() => [
    ...baseSections,
    ...productTypes.map((pt) => ({
      key: `product-type:${pt.id}`,
      labelEn: pt.nameEn,
      labelAr: pt.nameAr || pt.nameEn,
    })),
  ], [productTypes]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      loadSectionProducts(selectedSection);
    }
  }, [selectedSection]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [allProducts, allProductTypes] = await Promise.all([
        productService.getAll(),
        productTypeService.getAll(),
      ]);
      setProducts(allProducts || []);
      setProductTypes(allProductTypes || []);
    } catch (error) {
      console.error('Error loading home section data:', error);
      setMessage(isRtl ? 'تعذر تحميل البيانات' : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSectionProducts = async (sectionKey) => {
    try {
      setIsSectionLoading(true);
      setMessage('');
      const data = await homeSectionService.getSectionProducts(sectionKey);
      setSectionItems((data || []).map((item) => ({
        ...item,
        id: item.productId,
      })));
    } catch (error) {
      console.error('Error loading section products:', error);
      setMessage(isRtl ? 'تعذر تحميل منتجات القسم' : 'Failed to load section products');
      setSectionItems([]);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const selectedProductIds = useMemo(
    () => new Set(sectionItems.map((item) => item.productId || item.id)),
    [sectionItems]
  );

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products
      .filter((product) => !selectedProductIds.has(product.id))
      .filter((product) => matchesSelectedSection(product, selectedSection))
      .filter((product) => {
        if (!term) return true;
        return (
          product.nameEn?.toLowerCase().includes(term) ||
          product.nameAr?.toLowerCase().includes(term) ||
          product.brandEn?.toLowerCase().includes(term) ||
          product.categoryNameEn?.toLowerCase().includes(term)
        );
      })
      .slice(0, 20);
  }, [products, searchTerm, selectedProductIds, selectedSection]);

  const selectedSectionLabel = sections.find((section) => section.key === selectedSection);

  const addProduct = (product) => {
    setMessage('');
    setSectionItems((items) => [
      ...items,
      {
        id: product.id,
        productId: product.id,
        nameEn: product.nameEn,
        nameAr: product.nameAr,
        brandNameEn: product.brandEn,
        brandNameAr: product.brandAr || product.brandEn,
        imageUrl: product.imageUrlFull || product.imageUrl,
      },
    ]);
  };

  const removeProduct = (productId) => {
    setMessage('');
    setSectionItems((items) => items.filter((item) => (item.productId || item.id) !== productId));
  };

  const moveProduct = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sectionItems.length) return;

    setMessage('');
    setSectionItems((items) => {
      const next = [...items];
      const temp = next[index];
      next[index] = next[target];
      next[target] = temp;
      return next;
    });
  };

  const saveSection = async () => {
    try {
      setIsSaving(true);
      setMessage('');
      await homeSectionService.updateSectionProducts(selectedSection, sectionItems);
      await loadSectionProducts(selectedSection);
      setMessage(isRtl ? 'تم حفظ ترتيب القسم' : 'Section order saved');
    } catch (error) {
      console.error('Error saving section products:', error);
      setMessage(isRtl ? 'فشل حفظ القسم' : 'Failed to save section');
    } finally {
      setIsSaving(false);
    }
  };

  const clearSection = () => {
    setMessage('');
    setSectionItems([]);
  };

  const renderProductThumb = (url, name) => {
    const imageUrl = getProductImageUrl(url);
    return imageUrl ? (
      <img src={imageUrl} alt={name} className="home-section-thumb" />
    ) : (
      <div className="home-section-thumb home-section-thumb-empty" />
    );
  };

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading home sections...'} />;
  }

  return (
    <div className="home-sections-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة أقسام الصفحة الرئيسية' : 'Home Sections'}</h1>
            <p className="page-description">
              {isRtl
                ? 'اختر المنتجات التي تظهر في كل قسم ورتب ظهورها في المتجر.'
                : 'Choose which products appear in each store home section and control their order.'}
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={clearSection} disabled={isSaving || isSectionLoading}>
              {isRtl ? 'تفريغ القسم' : 'Clear Section'}
            </Button>
            <Button variant="primary" onClick={saveSection} disabled={isSaving || isSectionLoading}>
              {isSaving ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ الترتيب' : 'Save Order')}
            </Button>
          </div>
        </div>
      </div>

      {message && <div className="home-section-message">{message}</div>}

      <div className="home-sections-layout">
        <aside className="home-sections-sidebar card">
          <div className="home-section-panel-title">{isRtl ? 'الأقسام' : 'Sections'}</div>
          <div className="home-section-list">
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                className={`home-section-tab ${selectedSection === section.key ? 'active' : ''}`}
                onClick={() => setSelectedSection(section.key)}
              >
                <span>{isRtl ? section.labelAr : section.labelEn}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="home-sections-main">
          <section className="card home-section-card">
            <div className="home-section-card-header">
              <div>
                <div className="home-section-kicker">{isRtl ? 'الترتيب الحالي' : 'Current order'}</div>
                <h2>{isRtl ? selectedSectionLabel?.labelAr : selectedSectionLabel?.labelEn}</h2>
              </div>
              <span className="home-section-count">
                {isRtl ? `${sectionItems.length} منتج` : `${sectionItems.length} products`}
              </span>
            </div>

            {isSectionLoading ? (
              <Loading text={isRtl ? 'جاري تحميل القسم...' : 'Loading section...'} />
            ) : sectionItems.length === 0 ? (
              <div className="home-section-empty">
                {isRtl
                  ? 'لا توجد منتجات مخصصة. سيستخدم المتجر القائمة التلقائية لهذا القسم.'
                  : 'No custom products selected. The store will use the automatic fallback for this section.'}
              </div>
            ) : (
              <div className="home-section-selected-list">
                {sectionItems.map((item, index) => (
                  <div className="home-section-selected-item" key={item.productId || item.id}>
                    <div className="home-section-order">{index + 1}</div>
                    {renderProductThumb(item.imageUrl, item.nameEn)}
                    <div className="home-section-product-info">
                      <div className="home-section-product-name">{isRtl ? item.nameAr : item.nameEn}</div>
                      <div className="home-section-product-meta">
                        {isRtl ? (item.brandNameAr || item.brandNameEn) : item.brandNameEn}
                      </div>
                    </div>
                    <div className="home-section-row-actions">
                      <button type="button" onClick={() => moveProduct(index, -1)} disabled={index === 0}>↑</button>
                      <button type="button" onClick={() => moveProduct(index, 1)} disabled={index === sectionItems.length - 1}>↓</button>
                      <button type="button" className="danger" onClick={() => removeProduct(item.productId || item.id)}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="card home-section-card">
            <div className="home-section-card-header">
              <div>
                <div className="home-section-kicker">{isRtl ? 'إضافة منتجات' : 'Add products'}</div>
                <h2>{isRtl ? 'كتالوج المنتجات' : 'Product catalog'}</h2>
              </div>
              <div className="home-section-search">
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={isRtl ? 'ابحث عن منتج...' : 'Search products...'}
                />
              </div>
            </div>

            <div className="home-section-product-grid">
              {filteredProducts.map((product) => (
                <button
                  type="button"
                  className="home-section-product-option"
                  key={product.id}
                  onClick={() => addProduct(product)}
                >
                  {renderProductThumb(product.imageUrlFull || product.imageUrl, product.nameEn)}
                  <span>
                    <strong>{isRtl ? product.nameAr : product.nameEn}</strong>
                    <small>{isRtl ? (product.brandAr || product.brandEn) : product.brandEn}</small>
                  </span>
                  <b>+</b>
                </button>
              ))}
            </div>
          </section>
        </main>
      </div>

      <style>{`
        .home-sections-layout {
          display: grid;
          grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
          gap: var(--sp-5);
          align-items: start;
        }

        .home-sections-sidebar {
          padding: var(--sp-4);
          position: sticky;
          top: var(--sp-4);
        }

        .home-section-panel-title,
        .home-section-kicker {
          color: var(--text-muted);
          font-size: var(--fs-xs);
          font-weight: var(--fw-bold);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: var(--sp-3);
        }

        .home-section-list {
          display: flex;
          flex-direction: column;
          gap: var(--sp-2);
        }

        .home-section-tab {
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--text);
          border-radius: var(--r-md);
          padding: 0.75rem 0.9rem;
          text-align: start;
          cursor: pointer;
          font-weight: var(--fw-semibold);
          transition: all var(--duration-fast) var(--ease-smooth);
        }

        .home-section-tab.active,
        .home-section-tab:hover {
          border-color: var(--primary);
          background: color-mix(in srgb, var(--primary) 10%, var(--surface));
          color: var(--primary);
        }

        .home-sections-main {
          display: grid;
          gap: var(--sp-4);
        }

        .home-section-card {
          padding: var(--sp-4);
          min-width: 0;
        }

        .home-section-card-header {
          display: flex;
          justify-content: space-between;
          gap: var(--sp-4);
          align-items: center;
          margin-bottom: var(--sp-4);
        }

        .home-section-card-header h2 {
          margin: 0;
          font-size: var(--fs-xl);
          color: var(--text);
        }

        .home-section-count,
        .home-section-message {
          border-radius: 999px;
          background: color-mix(in srgb, var(--primary) 10%, var(--surface));
          color: var(--primary);
          font-weight: var(--fw-bold);
          padding: 0.45rem 0.8rem;
          font-size: var(--fs-sm);
        }

        .home-section-message {
          display: inline-flex;
          margin-bottom: var(--sp-4);
        }

        .home-section-empty {
          border: 1px dashed var(--border);
          color: var(--text-muted);
          border-radius: var(--r-lg);
          padding: var(--sp-6);
          text-align: center;
          background: var(--neutral-50);
        }

        .home-section-selected-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: var(--sp-2);
          max-height: 320px;
          min-height: 0;
          overflow-y: auto;
          padding-inline-end: 0.35rem;
        }

        .home-section-selected-item {
          display: grid;
          grid-template-columns: 30px 46px minmax(0, 1fr);
          align-items: center;
          gap: var(--sp-2);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          background: var(--surface);
          padding: var(--sp-2);
          min-width: 0;
        }

        .home-section-product-option {
          display: grid;
          grid-template-columns: 52px minmax(0, 1fr) auto;
          align-items: center;
          gap: var(--sp-3);
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
          background: var(--surface);
          padding: var(--sp-3);
        }

        .home-section-order {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: var(--neutral-100);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }

        .home-section-thumb {
          width: 52px;
          height: 52px;
          object-fit: cover;
          border-radius: var(--r-md);
          background: var(--neutral-100);
        }

        .home-section-selected-item .home-section-thumb {
          width: 46px;
          height: 46px;
        }

        .home-section-thumb-empty {
          border: 1px dashed var(--border);
        }

        .home-section-product-name {
          font-weight: var(--fw-bold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .home-section-product-meta,
        .home-section-product-option small {
          color: var(--text-muted);
          font-size: var(--fs-sm);
        }

        .home-section-row-actions {
          display: flex;
          gap: var(--sp-2);
          grid-column: 1 / -1;
          justify-content: flex-end;
          padding-top: 0.1rem;
        }

        .home-section-row-actions button,
        .home-section-product-option b {
          width: 32px;
          height: 32px;
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          background: var(--neutral-50);
          color: var(--text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--fw-bold);
        }

        .home-section-row-actions button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .home-section-row-actions button.danger {
          color: var(--danger);
        }

        .home-section-search input {
          width: 100%;
          min-width: 0;
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          padding: 0.65rem 0.85rem;
          background: var(--surface);
          color: var(--text);
        }

        .home-section-product-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--sp-2);
          max-height: none;
          min-height: 0;
          overflow-y: auto;
          padding-inline-end: 0.35rem;
        }

        .home-section-product-option {
          text-align: start;
          cursor: pointer;
          padding: var(--sp-2);
          border-radius: var(--r-md);
        }

        .home-section-product-option span {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .home-section-product-option strong {
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 980px) {
          .home-sections-layout {
            grid-template-columns: 1fr;
          }
          .home-sections-sidebar {
            position: static;
          }
          .home-section-list {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .home-section-card-header {
            align-items: stretch;
            flex-direction: column;
          }
          .home-section-search input {
            width: 100%;
            min-width: 0;
          }
          .home-section-product-grid {
            grid-template-columns: 1fr;
          }
          .home-section-selected-list {
            max-height: 360px;
          }
        }

        @media (max-width: 560px) {
          .home-section-list {
            grid-template-columns: 1fr;
          }
          .home-section-selected-item {
            grid-template-columns: auto 44px minmax(0, 1fr);
          }
          .home-section-thumb {
            width: 44px;
            height: 44px;
          }
        }
      `}</style>
    </div>
  );
};

export default HomeSectionsIndex;
